import { useState, useEffect, useRef } from 'react';
import { reverseGeocode } from '../services/location/geocodingService';

/**
 * Custom hook to fetch geocoded addresses for events
 * Prioritizes backend-provided addresses, falls back to client-side geocoding
 */
export function useGeocodedAddresses(events, enabled = true) {
  const [addresses, setAddresses] = useState({});
  const [loading, setLoading] = useState(true);
  const processedEventsRef = useRef(new Set());

  useEffect(() => {
    if (!enabled || events.length === 0) {
      setLoading(false);
      setAddresses({});
      return;
    }

    // Check if we've already processed these exact events
    const eventKeys = events.map(e => e.eventId || `${e.location?.latitude},${e.location?.longitude}`).join(',');
    if (processedEventsRef.current.has(eventKeys)) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const fetchAddresses = async () => {
      const addressMap = {};

      for (const event of events) {
        if (!event.location) continue;

        const key = `${event.location.latitude},${event.location.longitude}`;

        // Check if event already has address from backend
        if (event.metadata?.address) {
          addressMap[key] = event.metadata.address;
          continue;
        }

        // Fallback: fetch from frontend for old events without address
        try {
          const address = await reverseGeocode(
            event.location.latitude,
            event.location.longitude
          );
          if (cancelled) return;
          addressMap[key] = address;
        } catch (error) {
          console.error('Geocoding error:', error);
          if (cancelled) return;
          addressMap[key] = {
            short: 'Address unavailable',
            formatted: 'Address unavailable'
          };
        }
      }

      if (!cancelled) {
        setAddresses(addressMap);
        setLoading(false);
        processedEventsRef.current.add(eventKeys);
      }
    };

    fetchAddresses();

    return () => {
      cancelled = true;
    };
  }, [enabled, events]);

  return { addresses, loading };
}
